import type {
  OnboardingRuleStep,
  OnboardingRuleStepKey,
  OnboardingRulesProgressResponse,
} from "@ifgengineering/client-invest-sdk";

/** The web flows that collect the steps the server asks for before investing. */
export type ActionableRequirementKey =
  | "taxAndOccupation"
  | "accreditation"
  | "quiz"
  | "verification";

/** Cool-down is a wait the server has no step for, derived from when the account was opened. */
export type DealRequirementKey = ActionableRequirementKey | "coolDown";

export type DealRequirementStatus =
  | "not_started"
  | "in_progress"
  | "locked"
  | "submitted"
  | "satisfied";

export interface DealRequirement {
  key: DealRequirementKey;
  status: DealRequirementStatus;
  /** When a `locked` requirement opens. */
  until?: string;
}

/** The deal-anchored progress in the web's terms: the pre-app gate and the requirements after it. */
export interface DealAnchoredView {
  appUnlocked: boolean;
  complete: boolean;
  requirements: DealRequirement[];
  nextRequirement: DealRequirementKey | null;
}

const STEP_OWNER: Record<
  OnboardingRuleStepKey,
  "preApp" | ActionableRequirementKey
> = {
  personalInfo: "preApp",
  address: "preApp",
  emailPreferences: "preApp",
  socialAttribution: "preApp",
  taxInfo: "taxAndOccupation",
  sourceOfFunds: "taxAndOccupation",
  occupation: "taxAndOccupation",
  accreditation: "accreditation",
  quiz: "quiz",
  kyc: "verification",
};

export const COOL_DOWN_MS = 24 * 60 * 60 * 1000;

const isFuture = (iso: string | undefined, now: number): iso is string =>
  !!iso && new Date(iso).getTime() > now;

const requirementFrom = (
  key: ActionableRequirementKey,
  steps: OnboardingRuleStep[],
  now: number
): DealRequirement => {
  if (steps.every((step) => step.complete)) {
    // Identity is complete once submitted, but only satisfied once reviewed.
    const awaitingReview = steps.some((step) => step.reviewComplete === false);
    return { key, status: awaitingReview ? "submitted" : "satisfied" };
  }
  const locked = steps.find((step) => isFuture(step.blockedUntil, now));
  if (locked?.blockedUntil) {
    return { key, status: "locked", until: locked.blockedUntil };
  }
  const isStarted = steps.some((step) => step.status !== "not_started");
  return { key, status: isStarted ? "in_progress" : "not_started" };
};

const coolDownFrom = (
  investorCreatedAt: string | undefined,
  now: number
): DealRequirement => {
  const createdAt = investorCreatedAt
    ? new Date(investorCreatedAt).getTime()
    : Number.NaN;
  const endsAt = createdAt + COOL_DOWN_MS;
  return !Number.isNaN(endsAt) && endsAt > now
    ? {
        key: "coolDown",
        status: "locked",
        until: new Date(endsAt).toISOString(),
      }
    : { key: "coolDown", status: "satisfied" };
};

const isDone = ({ status }: DealRequirement): boolean =>
  status === "satisfied" || status === "submitted";

export interface BuildDealAnchoredViewArgs {
  progress: OnboardingRulesProgressResponse | null | undefined;
  investorCreatedAt?: string;
  /** Injected so the derivation stays pure and testable. */
  now?: number;
}

/** Groups the server's steps into the web's flows, in the server's order, and follows its `nextStep`. */
export const buildDealAnchoredView = ({
  progress,
  investorCreatedAt,
  now = Date.now(),
}: BuildDealAnchoredViewArgs): DealAnchoredView | null => {
  if (!progress) return null;

  const stepsByRequirement = new Map<
    ActionableRequirementKey,
    OnboardingRuleStep[]
  >();
  progress.steps.forEach((step) => {
    const owner = STEP_OWNER[step.key];
    if (owner === "preApp") return;
    stepsByRequirement.set(owner, [
      ...(stepsByRequirement.get(owner) ?? []),
      step,
    ]);
  });

  const requirements: DealRequirement[] = [
    ...Array.from(stepsByRequirement, ([key, steps]) =>
      requirementFrom(key, steps, now)
    ),
    coolDownFrom(investorCreatedAt, now),
  ];

  const nextOwner = progress.nextStep ? STEP_OWNER[progress.nextStep] : null;
  const pointed = requirements.find(({ key }) => key === nextOwner);
  const nextRequirement =
    (pointed ?? requirements.find((requirement) => !isDone(requirement)))
      ?.key ?? null;

  return {
    appUnlocked: progress.appUnlocked,
    complete: progress.complete,
    requirements,
    nextRequirement,
  };
};

export const isRequirementSatisfied = (
  view: DealAnchoredView | null | undefined,
  key: DealRequirementKey
): boolean =>
  view?.requirements.find((requirement) => requirement.key === key)?.status ===
  "satisfied";

export const isRuleStepComplete = (
  progress: OnboardingRulesProgressResponse | null | undefined,
  key: OnboardingRuleStepKey
): boolean => !!progress?.steps.find((step) => step.key === key)?.complete;
